import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import {
  fetchRxntSettings,
  fetchRxntPrescriptions,
  voidPrescription,
  refillPrescription,
  reinstatePrescription,
  resetPrescriptionForm,
} from '../../../../store/RxntSlice';
import RxntPrescriptionForm from './RxntPrescriptionForm';
import RxntService from '../../../../services/RxntService';
import { OK } from '../../../../utils/constant';
import TableGrid from '../../../../components/UI/TableGrid';
import Buttons from '../../../../components/UI/Buttons';
import PERMISSIONS from '../../../../utils/AclPermissions';
import { RXNT_VOID_CODE_OPTIONS, RXNT_VOID_CODE_PLACEHOLDER } from '../../../../utils/rxntVoidCodes';
import {
  canShowVoidForVisitPrescription,
  pendingRxntPrescriptionIdSet,
} from '../../../../utils/rxntUiUtils';

const RXNT_REINSTATE_UI_ENABLED = true;

const VOID_CODE_SELECT_OPTIONS = [RXNT_VOID_CODE_PLACEHOLDER, ...RXNT_VOID_CODE_OPTIONS];

const TABLE_HEADERS = [
  { label: 'Medication', key: 'medication' },
  { label: 'Sig', key: 'sig' },
  { label: 'Qty', key: 'quantity' },
  { label: 'Pharmacy', key: 'pharmacy' },
  { label: 'Provider', key: 'provider_name' },
  { label: 'Status', key: 'status' },
  { label: 'Date', key: 'date_display' },
  { label: 'Actions', key: 'action', type: 'Actions' },
];

const STATUS_LABELS = {
  PENDING: 'Pending',
  SENT: 'Sent',
  DELIVERED: 'Delivered',
  FILLED: 'Filled',
  CANCELLED: 'Cancelled',
  VOIDED: 'Voided',
  FAILED: 'Failed',
};

const normalizeClinicIdFromVisit = (visitDetails) => {
  const flat = visitDetails?.['0'] ?? visitDetails;
  const raw =
    flat?.assigned_clinic ??
    flat?.assigned_clinic_id ??
    visitDetails?.assigned_clinic ??
    visitDetails?.assigned_clinic_id;
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') {
    return raw.clinic_id ?? raw.id ?? null;
  }
  return raw;
};

const RxntPrescriptionsPage = ({
  visit_id,
  employeeId,
  visitDetails,
  Toast,
  Loader,
  fetchVisitList,
}) => {
  const ERROR_TOAST_TIMEOUT = 6000;
  const dispatch = useDispatch();
  const { settings, prescriptions } = useSelector((state) => state.rxnt);
  
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [selectedPrescription, setSelectedPrescription] = useState(null);
  const [voidModalOpen, setVoidModalOpen] = useState(false);
  const [voidComments, setVoidComments] = useState('');
  const [voidCode, setVoidCode] = useState('');
  const [reinstateModalOpen, setReinstateModalOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printImage, setPrintImage] = useState(null);
  const [printLoading, setPrintLoading] = useState(false);
  /** RXNT gateway pending worklist prescription ids (for void button on SENT rows). */
  const [pendingRxntIds, setPendingRxntIds] = useState(null);

  const visitFlat = visitDetails?.['0'] ?? visitDetails;
  const clinicId = normalizeClinicIdFromVisit(visitDetails);
  const providerId =
    visitFlat?.primary_provider_id ??
    visitFlat?.provider_id ??
    visitDetails?.primary_provider_id ??
    visitDetails?.provider_id;

  useEffect(() => {
    dispatch(fetchRxntSettings());
  }, [dispatch]);

  useEffect(() => {
    if (visit_id && settings.rx_enabled) {
      dispatch(fetchRxntPrescriptions(visit_id));
    }
  }, [dispatch, visit_id, settings.rx_enabled]);

  const rxntDoctorId = useMemo(() => {
    const rows = Array.isArray(prescriptions.data) ? prescriptions.data : [];
    const row = rows.find((p) => p.rxnt_doctor_id != null);
    return row?.rxnt_doctor_id != null ? Number(row.rxnt_doctor_id) : null;
  }, [prescriptions.data]);

  useEffect(() => {
    if (!settings.rx_enabled || !providerId || rxntDoctorId == null) {
      setPendingRxntIds(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await RxntService.listPrescriptions({
          tab: 'pending',
          provider_id: providerId,
          rxnt_doctor_id: rxntDoctorId,
          per_page: 300,
        });
        if (cancelled) return;
        setPendingRxntIds(
          res.code === OK ? pendingRxntPrescriptionIdSet(res.data) : new Set()
        );
      } catch {
        if (!cancelled) setPendingRxntIds(new Set());
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [settings.rx_enabled, providerId, rxntDoctorId, prescriptions.data]);

  const handlePrescriptionFormSuccess = ({ count } = {}) => {
    setFormModalOpen(false);
    dispatch(resetPrescriptionForm());
    const n = typeof count === 'number' ? count : 1;
    Toast.success(
      n === 1 ? 'Prescription sent successfully' : `${n} prescriptions sent successfully`
    );
  };

  const handleCreateNew = () => {
    setFormModalOpen(true);
    setSelectedPrescription(null);
  };

  const handleCloseForm = () => {
    setFormModalOpen(false);
    dispatch(resetPrescriptionForm());
  };

  const handleVoidClick = (prescription) => {
    setSelectedPrescription(prescription);
    setVoidComments('');
    setVoidCode('');
    setVoidModalOpen(true);
  };

  const handleVoidConfirm = async () => {
    if (!voidCode) {
      Toast.error('Please select a void reason', ERROR_TOAST_TIMEOUT);
      return;
    }
    if (!voidComments.trim()) {
      Toast.error('Please enter comments for voiding', ERROR_TOAST_TIMEOUT);
      return;
    }

    setActionLoading(true);
    try {
      await dispatch(voidPrescription({
        prescriptionId: selectedPrescription.id,
        comments: voidComments.trim(),
        voidCode,
      })).unwrap();
      Toast.success('Prescription voided successfully');
      setVoidModalOpen(false);
    } catch (error) {
      Toast.error(typeof error === 'string' ? error : (error?.message || 'Failed to void prescription'), ERROR_TOAST_TIMEOUT);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefill = async (prescription) => {
    if (!window.confirm('Are you sure you want to refill this prescription?')) {
      return;
    }

    setActionLoading(true);
    try {
      await dispatch(refillPrescription(prescription.id)).unwrap();
      Toast.success('Prescription refilled successfully');
    } catch (error) {
      Toast.error(typeof error === 'string' ? error : (error?.message || 'Failed to refill prescription'), ERROR_TOAST_TIMEOUT);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReinstateClick = (prescription) => {
    setSelectedPrescription(prescription);
    setReinstateModalOpen(true);
  };

  const handleReinstateConfirm = async () => {
    if (!selectedPrescription?.id) return;

    setActionLoading(true);
    try {
      await dispatch(reinstatePrescription(selectedPrescription.id)).unwrap();
      Toast.success('Prescription reinstated successfully');
      setReinstateModalOpen(false);
      if (visit_id && settings.rx_enabled) {
        dispatch(fetchRxntPrescriptions(visit_id));
      }
    } catch (error) {
      Toast.error(typeof error === 'string' ? error : (error?.message || 'Failed to reinstate prescription'), ERROR_TOAST_TIMEOUT);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = async (prescription) => {
    if (!prescription.rxnt_prescription_id) {
      Toast.error('This prescription has not been sent to RXNT yet', ERROR_TOAST_TIMEOUT);
      return;
    }

    setPrintLoading(true);
    setSelectedPrescription(prescription);
    if (Loader) Loader(true);
    try {
      const response = await RxntService.printPrescription(prescription.id);
      if (response.code === OK && response.data?.image) {
        setPrintImage(response.data.image);
        setPrintModalOpen(true);
      } else {
        Toast.error(response.message || 'Failed to get prescription print image', ERROR_TOAST_TIMEOUT);
      }
    } catch (error) {
      Toast.error(typeof error === 'string' ? error : (error?.message || 'Failed to get prescription print image'), ERROR_TOAST_TIMEOUT);
    } finally {
      setPrintLoading(false);
      if (Loader) Loader(false);
    }
  };

  const handleDownloadPrint = () => {
    if (!printImage || !selectedPrescription) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${printImage}`;
    link.download = `prescription_${selectedPrescription.drug_name || 'rx'}_${new Date().toISOString().slice(0, 10)}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClosePrintModal = () => {
    setPrintModalOpen(false);
    setPrintImage(null);
    setSelectedPrescription(null);
  };

  const handleBrowserPrint = () => {
    if (!printImage) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      Toast.error('Pop-up blocked. Allow pop-ups to print.', ERROR_TOAST_TIMEOUT);
      return;
    }
    printWindow.document.write(`
      <html>
        <head><title>Prescription - ${selectedPrescription?.drug_name || 'Print'}</title></head>
        <body style="margin:0; display:flex; justify-content:center; align-items:center;">
          <img src="data:image/png;base64,${printImage}" style="max-width:100%;" onload="window.print();window.close();" />
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const formatDate = useCallback((dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }, []);

  const fetchVisitPrescriptions = useCallback(() => {
    if (visit_id && settings.rx_enabled) {
      dispatch(fetchRxntPrescriptions(visit_id));
    }
  }, [dispatch, visit_id, settings.rx_enabled]);

  const handlersRef = useRef({});

  handlersRef.current = {
    handleVoidClick,
    handleRefill,
    handleReinstateClick,
    handlePrint,
    actionLoading,
    printLoading,
    selectedPrescription,
  };

  const tableRecords = useMemo(() => {
    const h = handlersRef;
    const tableData = [];
    const rows = Array.isArray(prescriptions.data) ? prescriptions.data : [];
    rows.forEach((prescription) => {
      const drugLine = [prescription.drug_name, prescription.drug_strength].filter(Boolean).join(' · ');
      const providerName = [prescription.provider?.first_name, prescription.provider?.last_name]
        .filter(Boolean)
        .join(' ')
        .trim() || '—';
      const qty =
        prescription.quantity != null
          ? `${prescription.quantity}${prescription.quantity_unit ? ` ${prescription.quantity_unit}` : ''}`
          : '—';

      const actionBtns = [];
      if (canShowVoidForVisitPrescription(prescription, pendingRxntIds)) {
        actionBtns.push({
          className: 'btn btn-icon btn-transparent-dark',
          iconType: 'Remove',
          title: 'Void',
          acl: PERMISSIONS.VISITS.EDIT,
          disabled: h.current.actionLoading,
          clickHandler: () => h.current.handleVoidClick(prescription),
        });
      }
      if (prescription.status === 'DELIVERED' || prescription.status === 'FILLED') {
        actionBtns.push({
          className: 'btn btn-icon btn-transparent-dark',
          iconType: 'AddPlus',
          title: 'Refill',
          acl: PERMISSIONS.VISITS.EDIT,
          disabled: h.current.actionLoading,
          clickHandler: () => h.current.handleRefill(prescription),
        });
      }
      if (RXNT_REINSTATE_UI_ENABLED && prescription.status === 'VOIDED') {
        actionBtns.push({
          className: 'btn btn-icon btn-transparent-dark',
          iconType: 'Update',
          title: 'Reinstate',
          acl: PERMISSIONS.VISITS.EDIT,
          disabled: h.current.actionLoading,
          clickHandler: () => h.current.handleReinstateClick(prescription),
        });
      }
      if (prescription.rxnt_prescription_id && prescription.status !== 'PENDING') {
        const printingThis =
          h.current.printLoading && h.current.selectedPrescription?.id === prescription.id;
        actionBtns.push({
          className: 'btn btn-icon btn-transparent-dark',
          iconType: 'Print',
          title: printingThis ? 'Loading...' : 'Print',
          acl: PERMISSIONS.VISITS.EDIT,
          disabled: h.current.actionLoading || h.current.printLoading,
          clickHandler: () => h.current.handlePrint(prescription),
        });
      }

      tableData.push({
        isHeading: false,
        rowId: `rxnt-visit-${prescription.id}`,
        data: {
          medication: drugLine || '—',
          sig: prescription.sig || '—',
          quantity: qty,
          pharmacy: prescription.pharmacy_name || '—',
          provider_name: providerName,
          status: STATUS_LABELS[prescription.status] || prescription.status || '—',
          date_display: formatDate(prescription.created_at),
          action: actionBtns,
        },
      });
    });

    return {
      tableHeaders: TABLE_HEADERS,
      tableRows: {
        status: 200,
        type: 'default',
        data: tableData,
      },
      pagination: {
        page: 1,
        perPage: 25,
        totalRecords: tableData.length,
        sort_by: 'date_display',
        sort_order: 'desc',
      },
    };
  }, [prescriptions.data, pendingRxntIds, actionLoading, printLoading, selectedPrescription, formatDate]);

  const gridEditProps = useMemo(
    () => ({ activeEditId: null, onChangeFormDataInEdit: () => {} }),
    []
  );

  if (!settings.rx_enabled) {
    return (
      <div className="rxnt-container">
        <div className="rxnt-disabled-message">
          <div className="rxnt-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              <path d="M12 8v4"/>
              <path d="M12 16h.01"/>
            </svg>
          </div>
          <h3>RXNT ePrescribing Not Enabled</h3>
          <p>Contact your administrator to enable RXNT ePrescribing for your organization.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-100 rxnt-container">
      <div className="card cardhtmd w-100 rxnt-cardhtmd">
        <div className="card-header p-2 sh_basecardHeader">
          <div className="d-flex w-100 justify-content-between align-items-center">
            <div>RXNT ePrescriptions</div>
            <div className="d-flex align-items-center gap-2">
              {/* <button
                className="btn btn-outline-primary sh_btnoutline d-inline-flex align-items-center"
                onClick={() => window.open(RXNT_PORTAL_URL, '_blank')}
                title="Opens RXNT in a new tab — sign in there (RXNT password or Sign in with Microsoft per your practice SSO)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="me-1">
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
                  <polyline points="15 3 21 3 21 9"/>
                  <line x1="10" y1="14" x2="21" y2="3"/>
                </svg>
                Go to RXNT
              </button> */}
              <button
                className="btn btn-primary sh_btnprimary d-inline-flex align-items-center"
                onClick={handleCreateNew}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                <span className="ml-1">Add Prescription</span>
              </button>
            </div>
          </div>
        </div>
        <div className="card-body p-3 rxnt-card-body">
          {prescriptions.loading ? (
            <div className="rxnt-loading">Loading prescriptions...</div>
          ) : prescriptions.error ? (
            <div className="rxnt-error">
              Error loading prescriptions: {prescriptions.error}
            </div>
          ) : prescriptions.data.length === 0 ? (
            <div className="rxnt-empty py-4 text-center text-muted">
              <p className="mb-0">No records found.</p>
            </div>
          ) : (
            <div className="rxnt-prescriptions-list sh_cardBody p-0 sh_cardbglight">
              <TableGrid
                hasPagination={false}
                needCardBody={false}
                fetchTableRecords={fetchVisitPrescriptions}
                gridEditProps={gridEditProps}
                customClass="rxnt-table rxnt-visit-rx-tablegrid"
                {...tableRecords}
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        show={formModalOpen}
        onHide={handleCloseForm}
        centered
        size="xl"
        backdrop="static"
        className="sh_customModal rxnt-add-prescription-modal"
      >
        <div className="sh_modalBox">
          <Modal.Header closeButton>
            <Modal.Title>
              <h6 className="text-lg mb-0 font-weight-bold">Add Prescription</h6>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <RxntPrescriptionForm
              visitId={visit_id}
              employeeId={employeeId}
              clinicId={clinicId}
              providerId={providerId}
              visitDetails={visitDetails}
              onClose={handleCloseForm}
              onSubmitSuccess={handlePrescriptionFormSuccess}
              Toast={Toast}
              Loader={Loader}
              fetchVisitList={fetchVisitList}
              embedded
            />
          </Modal.Body>
        </div>
      </Modal>

      <Modal
        show={voidModalOpen}
        onHide={() => setVoidModalOpen(false)}
        centered
        backdrop="static"
        className="sh_customModal"
      >
        <div className="sh_modalBox">
          <Modal.Header closeButton>
            <Modal.Title>
              <h6 className="text-lg mb-0 font-weight-bold">Void Prescription</h6>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              You are about to void the prescription for{' '}
              <strong>{selectedPrescription?.drug_name}</strong>.
            </p>
            <div className="form-group">
              <label className="mb-1 text-sm opacity-75">Void reason <span className="text-danger">*</span></label>
              <select
                className="form-control form-control-solid"
                value={voidCode}
                onChange={(e) => setVoidCode(e.target.value)}
                disabled={actionLoading}
              >
                {VOID_CODE_SELECT_OPTIONS.map((opt) => (
                  <option key={opt.value || 'placeholder'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="mb-1 text-sm opacity-75">Comments <span className="text-danger">*</span></label>
              <textarea
                className="form-control form-control-solid"
                value={voidComments}
                onChange={(e) => setVoidComments(e.target.value)}
                placeholder="Enter additional comments for voiding this prescription..."
                rows={3}
                disabled={actionLoading}
              />
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <button
              className="btn btn-secondary sh_btnsecondary mr-2"
              onClick={() => setVoidModalOpen(false)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary sh_btnprimary"
              onClick={handleVoidConfirm}
              disabled={actionLoading || !voidCode || !voidComments.trim()}
            >
              {actionLoading ? 'Voiding...' : 'Void Prescription'}
            </button>
          </Modal.Footer>
        </div>
      </Modal>

      {RXNT_REINSTATE_UI_ENABLED && (
        <Modal
          show={reinstateModalOpen}
          onHide={() => setReinstateModalOpen(false)}
          centered
          backdrop="static"
          className="sh_customModal"
        >
          <div className="sh_modalBox">
            <Modal.Header closeButton>
              <Modal.Title>
                <h6 className="text-lg mb-0 font-weight-bold">Reinstate prescription</h6>
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p className="mb-0">
                Are you sure you want to reinstate{' '}
                <strong>{selectedPrescription?.drug_name}</strong>? This will restore the prescription in Snap and
                RXNT (when applicable).
              </p>
            </Modal.Body>
            <Modal.Footer className="d-flex justify-content-center">
              <button
                type="button"
                className="btn btn-secondary sh_btnsecondary mr-2"
                onClick={() => setReinstateModalOpen(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary sh_btnprimary"
                onClick={handleReinstateConfirm}
                disabled={actionLoading}
              >
                {actionLoading ? 'Reinstating…' : 'Reinstate'}
              </button>
            </Modal.Footer>
          </div>
        </Modal>
      )}

      <Modal
        show={printModalOpen}
        onHide={handleClosePrintModal}
        centered
        size="lg"
        backdrop="static"
        className="sh_customModal"
      >
        <div className="sh_modalBox">
          <Modal.Header closeButton>
            <Modal.Title>
              <h6 className="text-lg mb-0 font-weight-bold">
                Prescription - {selectedPrescription?.drug_name}
              </h6>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            {printImage ? (
              <img
                src={`data:image/png;base64,${printImage}`}
                alt="Prescription"
                style={{ maxWidth: '100%', maxHeight: '70vh', border: '1px solid #ddd' }}
              />
            ) : (
              <div>Loading prescription image...</div>
            )}
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <Buttons
              className="btn btn-secondary sh_btnsecondary mr-2"
              label="Close"
              clickHandler={handleClosePrintModal}
            />
            <Buttons
              className="btn btn-primary sh_btnprimary mr-2"
              label="Download"
              clickHandler={handleDownloadPrint}
              disabled={!printImage}
              acl={PERMISSIONS.VISITS.EDIT}
            />
            <Buttons
              className="btn btn-primary sh_btnprimary"
              label="Print"
              clickHandler={handleBrowserPrint}
              disabled={!printImage}
              acl={PERMISSIONS.VISITS.EDIT}
            />
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default RxntPrescriptionsPage;
